import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { Box, Stack } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import Paper from "@mui/material/Paper";
import CrossButton from "assets/Primary_white.svg";
import ThemeContext from "components/app/Context";
import Button from "components/common/Button";
import NoPlansBanner from "components/common/NoPlansBanner";
import { setFeedbackDetails } from "components/common/Snackbar/snackbar.slice";
import Typography from "components/common/Typography";
import { useTranslate } from "hooks/useTranslate";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { accessTypes } from "utils/CommonConstants";
import { pageNames } from "utils/Types";
import { updateBookmarkedInncodesAsync } from "../dashboard/Dashboard.slice";
import PropertiesBookmark from "./PropertyView/PropertiesBookmark";

const Item = ({ data }: any) => {
  const dispatch = useAppDispatch();
  const { isMobileScreen } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(false);
  const { bookmarkedProperties } = useAppSelector((state) => state.aboveProperty);
  const handleBookmarking = async () => {
    await dispatch(
      updateBookmarkedInncodesAsync({
        bookmarks: bookmarkedProperties?.filter((item: any) => item?.innCode !== data?.innCode)
      })
    );
    dispatch(
      setFeedbackDetails({
        shouldShow: true,
        variant: "success",
        setState: setFeedbackDetails({ shouldShow: false }),
        message: "Property removed from bookmarked."
      })
    );
  };

  return (
    <Paper
      key={data?.id}
      elevation={isMobileScreen ? 0 : 1}
      sx={{
        minWidth: "90%",
        padding: "16px",
        cursor: "pointer",
        mr: isMobileScreen ? "8px" : 0,
        mb: isMobileScreen ? 0 : "24px",
        border: isMobileScreen ? `1px solid ${tokens.Border.General.Tertiary}` : "none"
      }}
      onClick={() => navigate(`/property/${data?.innCode}?parentTab=Property+Plans`)}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="flex-start"
        sx={{ borderBottom: `1px solid ${tokens.Border.General.Tertiary}`, mb: "12px", pb: "12px", minHeight: "80px" }}
      >
        <Stack spacing={1} flex={1}>
          <Typography variant="body2" showBold color={tokens.Text.General.PrimaryHeadline}>
            {data?.name}
          </Typography>
          <Typography variant="label" color={tokens.Text.General.PrimaryHeadline}>
            {data?.innCode}
          </Typography>
        </Stack>
        <Button
          sx={{ color: tokens.Icon.General.Primary }}
          iconButton={true}
          icon={CrossButton}
          height="30"
          accessType={accessTypes.update}
          onClick={(e) => {
            e.stopPropagation();
            handleBookmarking();
          }}
        ></Button>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          cursor: "pointer",
          "& .MuiSvgIcon-root": {
            transform: showDetails ? "rotate(90deg)" : ""
          }
        }}
        onClick={(e) => {
          e.stopPropagation();
          setShowDetails(!showDetails);
        }}
      >
        <ArrowRightIcon sx={{ color: tokens.Icon.General.Primary, ml: "-8px" }} />
        <Typography showBold variant="body2" color={tokens.Text.General.PrimaryHeadline}>
          Plans
        </Typography>
      </Stack>
      <Collapse in={showDetails}>
        <Stack spacing={1} sx={{ mt: "12px" }}>
          <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
            <Typography variant="body2" color={tokens.Text.General.PrimaryHeadline}>
              New
            </Typography>
            <Typography variant="body2" color={tokens.Text.General.PrimaryHeadline} showBold>
              {data?.propertySnapshot?.[0]?.new || 0}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
            <Typography variant="body2" color={tokens.Text.General.PrimaryHeadline}>
              In Progress
            </Typography>
            <Typography variant="body2" color={tokens.Text.General.PrimaryHeadline} showBold>
              {data?.propertySnapshot?.[0]?.inProgress || 0}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
            <Typography variant="body2" color={tokens.Text.General.PrimaryHeadline}>
              Completed w/i 90 days
            </Typography>
            <Typography variant="body2" color={tokens.Text.General.PrimaryHeadline} showBold>
              {data?.propertySnapshot?.[0]?.completedLast90Days || 0}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
            <Typography variant="body2" color={tokens.Text.Feedback.Overdue}>
              Overdue
            </Typography>
            <Typography variant="body2" color={tokens.Text.Feedback.Overdue} showBold>
              {data?.propertySnapshot?.[0]?.overdue || 0}
            </Typography>
          </Stack>
        </Stack>
      </Collapse>
    </Paper>
  );
};

const BookmarkedProperties = () => {
  const translate = useTranslate();
  const { isMobileScreen } = useContext(ThemeContext);
  const { bookmarkedProperties } = useAppSelector((state) => state.aboveProperty);
  const [openDialog, setOpenDialog] = useState(false);

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  return (
    <>
      <Typography variant="h5" showBold color={tokens.Text.General.PrimaryHeadline} sx={{ mb: "32px" }}>
        {translate("common.bookmarkedHotels")}
      </Typography>
      {bookmarkedProperties?.length ? (
        <>
          <Stack
            direction={"column"}
            sx={{
              overflowX: "auto",
              "&": {
                msOverflowStyle: "none"
              },
              "&::-webkit-scrollbar": {
                display: "auto"
              },
              gap: isMobileScreen ? "15px" : "none",
              maxHeight: isMobileScreen ? "55vh" : "none"
            }}
          >
            {bookmarkedProperties?.map((b: any) => (
              <Item key={b?.id} data={b} />
            ))}
          </Stack>
          <Box width={"100%"} display={"flex"} marginTop={"auto"} paddingTop="15px">
            <Button
              width="100%"
              size="Medium"
              variant="primary"
              accessType={accessTypes.update}
              label={translate("dashboard.bookmark.hotels")}
              onClick={() => setOpenDialog(true)}
            />
          </Box>
        </>
      ) : (
        <NoPlansBanner
          pageName={pageNames.aboveProperty}
          type={pageNames.aboveProperty}
          isDashboard
          buttons={
            <Button
              size="Small"
              variant="primaryWhite"
              accessType={accessTypes.update}
              label={translate("dashboard.bookmark.hotels")}
              onClick={() => setOpenDialog(true)}
            />
          }
        />
      )}
      <PropertiesBookmark
        openBookmarkPropertiesDialog={openDialog}
        handleCloseDialog={handleDialogClose}
      ></PropertiesBookmark>
    </>
  );
};

export default BookmarkedProperties;
